import React, { useCallback, useEffect, useRef } from 'react'
import { AppAsyncSelectV2, AppButton, AppTextInput } from '@easyllama/ui-components'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/atoms/shadcn/card'

export const KIOSK_LOOKUP_FIELD_ID = 'kiosk-lookup-field'

const CARD_CLASSNAME = 'py-6 px-4 sm:px-8 border-llama-grey-8 rounded-2xl'
const CARD_TITLE_CLASSNAME = 'text-lg font-bold  text-llama-grey-7 leading-[150%]'
const CARD_DESCRIPTION_CLASSNAME = 'text-llama-grey-5 text-base leading-[150%] '

const makeStaticFetcher = (options) => async (inputValue) => {
  const q = typeof inputValue === 'string' ? inputValue.toLowerCase() : ''
  const filtered = q ? options.filter(o => o.label?.toLowerCase().includes(q)) : options
  return { options: filtered, pagination: { currentPage: 1, totalPages: 1 } }
}

// Matches legacy copy exactly (show.html.erb), keyed by @kiosk_link.lookup_mode.
const LOOKUP_MODE_CONFIG = {
  email: {
    instruction: 'To get started, please enter your email. If it cannot be found, click the link below to get started.',
    label: 'Email',
    inputType: 'email',
    placeholder: 'e.g. example@gmail.com',
  },
  integration_user_id: {
    instruction: <>To get started, please enter your <b>employee ID</b>.</>,
    label: 'Employee ID',
    inputType: 'text',
    placeholder: 'e.g. A123',
  },
  internal_id: {
    instruction: <>To get started, please enter your <b>employee ID</b>.</>,
    label: 'Employee ID',
    inputType: 'text',
    placeholder: 'e.g. A123',
  },
  dropdown: {
    instruction: 'To get started, please select your name in the dropdown below.',
    label: 'Your name',
  },
}

export const VALID_LOOKUP_MODES = Object.keys(LOOKUP_MODE_CONFIG)

export const requiredErrorFor = (lookupMode) =>
  `${(LOOKUP_MODE_CONFIG[lookupMode] ?? LOOKUP_MODE_CONFIG.dropdown).label} is required.`

const KioskLookupForm = ({
  lookupMode,
  welcomeMessage,
  addLearner,
  learners = [],
  textValue,
  onTextValueChange,
  selectedLearner,
  onSelectedLearnerChange,
  error,
  submitting,
  onSubmit,
  onAddLearnerClick,
  addLearnerButtonRef,
}) => {
  const isDropdown = lookupMode === 'dropdown'
  const modeConfig = LOOKUP_MODE_CONFIG[lookupMode] ?? LOOKUP_MODE_CONFIG.dropdown
  const fieldWrapRef = useRef(null)

  const learnerOptions = learners.map(l => ({ label: l.name, value: l.uuid }))
  const fetchLearnerOptions = useCallback(makeStaticFetcher(learnerOptions), [learnerOptions])

  // CAT-1495: when a field/server error appears, move focus to the invalid control so
  // keyboard and AT users land on the problem instead of staying on Continue.
  useEffect(() => {
    if (!error) return
    const focusInvalidField = () => {
      const root = fieldWrapRef.current
      if (!root) return
      const control = root.querySelector(`#${KIOSK_LOOKUP_FIELD_ID}, [role="combobox"], input, select`)
      control?.focus?.()
    }
    const frameId = window.requestAnimationFrame(focusInvalidField)
    return () => window.cancelAnimationFrame(frameId)
  }, [error])

  return (
    <Card className={`w-full flex-1 gap-0 ${CARD_CLASSNAME} bg-white`}>
      <CardHeader className='p-0 text-center'>
        <CardTitle as='h2' className={CARD_TITLE_CLASSNAME}>Start Your Online Training</CardTitle>
        <CardDescription className={CARD_DESCRIPTION_CLASSNAME}>{modeConfig.instruction}</CardDescription>
      </CardHeader>

      <div className='my-6 border-b border-llama-grey-8' />

      <CardContent className='p-0'>
        <form onSubmit={onSubmit} noValidate className='flex flex-col items-center gap-4'>
          {welcomeMessage && (
            <p className='text-sm text-llama-grey-7 bg-llama-blue-2 border border-llama-grey-8 rounded-lg py-3 px-4 w-full'>
              {welcomeMessage}
            </p>
          )}

          <div ref={fieldWrapRef} className='w-full max-w-xs'>
            {isDropdown ? (
              <AppAsyncSelectV2
                id={KIOSK_LOOKUP_FIELD_ID}
                label={modeConfig.label}
                labelClassName='sr-only'
                multiSelect={false}
                fetchOptions={fetchLearnerOptions}
                paginate={false}
                value={selectedLearner}
                onChange={onSelectedLearnerChange}
                placeholder='Click here to select your name'
                emptyMessage='No learners found'
                error={error}
              />
            ) : (
              <AppTextInput
                id={KIOSK_LOOKUP_FIELD_ID}
                label={modeConfig.label}
                type={modeConfig.inputType}
                placeholder={modeConfig.placeholder}
                value={textValue}
                onChange={e => onTextValueChange(e.target.value)}
                error={error}
                required
              />
            )}
          </div>

          {addLearner && (
            <div className='text-xs text-llama-grey-5'>
              Haven&apos;t started yet?{' '}
              <button
                type='button'
                ref={addLearnerButtonRef}
                onClick={onAddLearnerClick}
                className='text-llama-purple-4 underline cursor-pointer bg-transparent border-0 p-0 font-inherit text-xs'
              >
                Click here
              </button>
              {' '}
              to start a new training.
            </div>
          )}

          <AppButton
            type='submit'
            id='kiosk-continue-button'
            disabled={submitting}
            className='w-full max-w-xs mt-2'
          >
            {submitting ? 'Please wait…' : 'Continue'}
          </AppButton>
        </form>
      </CardContent>
    </Card>
  )
}

export default KioskLookupForm
